import React, { useEffect, useRef } from 'react';
import maplibregl, { Map } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

export interface TeAraroaMapProps {
  /** Initial center coordinates [lng, lat] */
  center?: [number, number];
  /** Initial zoom level */
  zoom?: number;
  /** Map height (CSS value) */
  height?: string;
  /** Callback when map is loaded and ready */
  onMapLoad?: (map: Map) => void;
  /** Callback when map is clicked */
  onMapClick?: (event: maplibregl.MapMouseEvent) => void;
  /** Custom class name for the map container */
  className?: string;
  /** Whether to show navigation controls */
  showControls?: boolean;
}

/**
 * Base Te Araroa map component with LINZ basemap and all trail layers.
 * Provides a consistent map experience across all admin pages.
 */
const TeAraroaMap: React.FC<TeAraroaMapProps> = ({
  center = [172.6, -43.5], // Default: centered on New Zealand
  zoom = 5,
  height = '600px',
  onMapLoad,
  onMapClick,
  className = '',
  showControls = true
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<Map | null>(null);
  const initializedRef = useRef(false);
  const onMapLoadRef = useRef(onMapLoad);
  const onMapClickRef = useRef(onMapClick);

  // Keep refs updated with latest callbacks
  useEffect(() => {
    onMapLoadRef.current = onMapLoad;
  }, [onMapLoad]);

  useEffect(() => {
    onMapClickRef.current = onMapClick;
  }, [onMapClick]);

  useEffect(() => {
    if (!mapContainer.current || initializedRef.current) return;

    initializedRef.current = true;

    const initMap = async () => {
      try {
        if (process.env.NODE_ENV === 'development') {
          console.debug('Initializing Te Araroa map...');
        }

        // Fetch LINZ style to avoid CORS issues
        const response = await fetch('https://basemaps.linz.govt.nz/v1/styles/topographic-v2.json?api=c01k7rvxrvpcw2rzw7zh3vqtqzy');

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const style = await response.json();

        // Add custom Te Araroa sprite
        style.sprite = `${window.location.origin}/sprites/ta_sprite`;

        map.current = new maplibregl.Map({
          container: mapContainer.current!,
          center: center,
          zoom: zoom,
          style: style
        });

        if (process.env.NODE_ENV === 'development') {
          console.debug('Map initialized successfully');
        }

        // Add navigation controls if requested
        if (showControls) {
          map.current.addControl(new maplibregl.NavigationControl(), 'top-right');
        }

        // Wait for map to load, then add Te Araroa layers
        map.current.on('load', () => {
          if (!map.current) return;

          // Check if sources already exist (prevents duplicate add in StrictMode)
          if (map.current.getSource('trail-main')) {
            if (process.env.NODE_ENV === 'development') {
              console.debug('Trail layers already added, skipping...');
            }

            // Still call onMapLoad callback
            if (onMapLoadRef.current) {
              onMapLoadRef.current(map.current);
            }
            return;
          }

          if (process.env.NODE_ENV === 'development') {
            console.debug('Adding Te Araroa trail data layers...');
          }

          const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
          const membersApiUrl = process.env.REACT_APP_MEMBERS_API_URL || 'http://localhost:5130';

          // Main Trail (Polyline)
          map.current.addSource('trail-main', {
            type: 'geojson',
            data: `${membersApiUrl}/api/trail-map/trail`
          });

          // Main trail - solid lines (all except strait)
          map.current.addLayer({
            id: 'trail-main-line',
            type: 'line',
            source: 'trail-main',
            filter: ['!=', ['downcase', ['get', 'STATUS']], 'strait'],
            paint: {
              'line-color': [
                'match',
                ['downcase', ['get', 'STATUS']],
                'open', '#000000',
                'road', '#FF5500',
                'seasonal', '#FFFF00',
                'water', '#0070FF',
                '#000000' // default
              ],
              'line-width': 2
            }
          });

          // Cook Strait crossing - dashed line
          map.current.addLayer({
            id: 'trail-main-dashed',
            type: 'line',
            source: 'trail-main',
            filter: ['==', ['downcase', ['get', 'STATUS']], 'strait'],
            paint: {
              'line-color': '#0070FF',
              'line-width': 2,
              'line-dasharray': [2, 2]
            }
          });

          // Bypasses (Polyline)
          map.current.addSource('trail-bypasses', {
            type: 'geojson',
            data: `${membersApiUrl}/api/trail-map/bypasses`
          });

          map.current.addLayer({
            id: 'trail-bypasses-line',
            type: 'line',
            source: 'trail-bypasses',
            paint: {
              'line-color': '#C500FF',
              'line-width': 2.5,
              'line-dasharray': [2, 2]
            }
          });

          // Layer 0: Drinking Water (Points)
          map.current.addSource('drinking-water', {
            type: 'geojson',
            data: `${apiBaseUrl}/api/ArcGISProxy/layer/0/query?where=1=1&outFields=*&returnGeometry=true`
          });

          map.current.addLayer({
            id: 'drinking-water-points',
            type: 'symbol',
            source: 'drinking-water',
            layout: {
              'icon-image': [
                'step',
                ['zoom'],
                '',
                13, 'well_pnt_small_fill',
                18, 'well_pnt_small_fill'
              ],
              'icon-size': 1.0,
              'icon-allow-overlap': false
            }
          });

          // Layer 1: DOC Huts & Campsites (Points)
          map.current.addSource('doc-huts', {
            type: 'geojson',
            data: `${apiBaseUrl}/api/ArcGISProxy/layer/1/query?where=1=1&outFields=*&returnGeometry=true`
          });

          map.current.addLayer({
            id: 'doc-huts-points',
            type: 'symbol',
            source: 'doc-huts',
            layout: {
              'icon-image': [
                'step',
                ['zoom'],
                '',
                6, 'building_pnt_hut',
                18, 'building_pnt_hut'
              ],
              'icon-size': 1.0,
              'icon-allow-overlap': false,
              'text-field': [
                'step',
                ['zoom'],
                '',
                10, ['get', 'name'],
                18, ['get', 'name']
              ],
              'text-size': 10,
              'text-anchor': 'left',
              'text-offset': [1, 0],
              'text-font': ['Roboto Bold'],
              'text-allow-overlap': false
            },
            paint: {
              'text-color': '#494444',
              'text-halo-color': '#FFFFFF',
              'text-halo-width': 1.5
            }
          });

          // Layer 2: Private Campsites (Points)
          map.current.addSource('private-campsites', {
            type: 'geojson',
            data: `${apiBaseUrl}/api/ArcGISProxy/layer/2/query?where=1=1&outFields=*&returnGeometry=true`
          });

          map.current.addLayer({
            id: 'private-campsites-points',
            type: 'symbol',
            source: 'private-campsites',
            layout: {
              'icon-image': [
                'step',
                ['zoom'],
                '',
                7, 'private_campsite',
                18, 'private_campsite'
              ],
              'icon-size': 1.0,
              'icon-allow-overlap': false,
              'text-field': [
                'step',
                ['zoom'],
                '',
                9, ['get', 'name'],
                18, ['get', 'name']
              ],
              'text-size': 10,
              'text-anchor': 'left',
              'text-offset': [1, 0],
              'text-font': ['Roboto Bold'],
              'text-allow-overlap': true
            },
            paint: {
              'text-color': '#494444',
              'text-halo-color': '#FFFFFF',
              'text-halo-width': 1.5
            }
          });

          // Layer 3: YHA (Points)
          map.current.addSource('yha', {
            type: 'geojson',
            data: `${apiBaseUrl}/api/ArcGISProxy/layer/3/query?where=1=1&outFields=*&returnGeometry=true`
          });

          map.current.addLayer({
            id: 'yha-points',
            type: 'symbol',
            source: 'yha',
            layout: {
              'icon-image': [
                'step',
                ['zoom'],
                '',
                7, 'yha',
                18, 'yha'
              ],
              'icon-size': 1.0,
              'icon-allow-overlap': false,
              'text-field': [
                'step',
                ['zoom'],
                '',
                12, ['get', 'name'],
                18, ['get', 'name']
              ],
              'text-size': 10,
              'text-anchor': 'left',
              'text-offset': [1, 0],
              'text-font': ['Roboto Bold'],
              'text-allow-overlap': true
            },
            paint: {
              'text-color': '#494444',
              'text-halo-color': '#FFFFFF',
              'text-halo-width': 1.5
            }
          });

          // KM Markers (Points) - Zoom-based filtering
          map.current.addSource('km-markers', {
            type: 'geojson',
            data: `${membersApiUrl}/api/trail-map/km-markers`
          });

          // All markers at zoom >= 10
          map.current.addLayer({
            id: 'km-markers-all-circle',
            type: 'circle',
            source: 'km-markers',
            filter: ['>=', ['zoom'], 10],
            paint: {
              'circle-radius': 2.0,
              'circle-color': '#000000',
              'circle-stroke-width': 1,
              'circle-stroke-color': '#FFFFFF'
            }
          });

          map.current.addLayer({
            id: 'km-markers-all-text',
            type: 'symbol',
            source: 'km-markers',
            filter: ['>=', ['zoom'], 10],
            layout: {
              'text-field': ['get', 'KM'],
              'text-size': 14,
              'text-anchor': 'left',
              'text-offset': [1, 0],
              'text-font': ['Roboto Bold'],
              'text-allow-overlap': false
            },
            paint: {
              'text-color': '#494444',
              'text-halo-color': '#FFFFFF',
              'text-halo-width': 1.5
            }
          });

          // Every 10km at zoom 8-10
          map.current.addLayer({
            id: 'km-markers-10-circle',
            type: 'circle',
            source: 'km-markers',
            filter: [
              'all',
              ['>=', ['zoom'], 8],
              ['<', ['zoom'], 10],
              ['==', ['%', ['get', 'KM'], 10], 0]
            ],
            paint: {
              'circle-radius': 2.0,
              'circle-color': '#000000',
              'circle-stroke-width': 1,
              'circle-stroke-color': '#FFFFFF'
            }
          });

          map.current.addLayer({
            id: 'km-markers-10-text',
            type: 'symbol',
            source: 'km-markers',
            filter: [
              'all',
              ['>=', ['zoom'], 8],
              ['<', ['zoom'], 10],
              ['==', ['%', ['get', 'KM'], 10], 0]
            ],
            layout: {
              'text-field': ['get', 'KM'],
              'text-size': 12,
              'text-anchor': 'left',
              'text-offset': [1, 0],
              'text-font': ['Roboto Bold'],
              'text-allow-overlap': true
            },
            paint: {
              'text-color': '#494444',
              'text-halo-color': '#FFFFFF',
              'text-halo-width': 1.5
            }
          });

          // Every 50km at zoom 6-8
          map.current.addLayer({
            id: 'km-markers-50-circle',
            type: 'circle',
            source: 'km-markers',
            filter: [
              'all',
              ['>=', ['zoom'], 6],
              ['<', ['zoom'], 8],
              ['==', ['%', ['get', 'KM'], 50], 0],
              ['!=', ['get', 'KM'], 0]
            ],
            paint: {
              'circle-radius': 2.0,
              'circle-color': '#000000',
              'circle-stroke-width': 1,
              'circle-stroke-color': '#FFFFFF'
            }
          });

          map.current.addLayer({
            id: 'km-markers-50-text',
            type: 'symbol',
            source: 'km-markers',
            filter: [
              'all',
              ['>=', ['zoom'], 6],
              ['<', ['zoom'], 8],
              ['==', ['%', ['get', 'KM'], 50], 0],
              ['!=', ['get', 'KM'], 0]
            ],
            layout: {
              'text-field': ['get', 'KM'],
              'text-size': 12,
              'text-anchor': 'left',
              'text-offset': [1, 0],
              'text-font': ['Roboto Bold'],
              'text-allow-overlap': true
            },
            paint: {
              'text-color': '#494444',
              'text-halo-color': '#FFFFFF',
              'text-halo-width': 1.5
            }
          });

          // Every 100km at zoom 5-6
          map.current.addLayer({
            id: 'km-markers-100-circle',
            type: 'circle',
            source: 'km-markers',
            filter: [
              'all',
              ['>=', ['zoom'], 5],
              ['<', ['zoom'], 6],
              ['==', ['%', ['get', 'KM'], 100], 0],
              ['!=', ['get', 'KM'], 0]
            ],
            paint: {
              'circle-radius': 2.0,
              'circle-color': '#000000',
              'circle-stroke-width': 1,
              'circle-stroke-color': '#FFFFFF'
            }
          });

          map.current.addLayer({
            id: 'km-markers-100-text',
            type: 'symbol',
            source: 'km-markers',
            filter: [
              'all',
              ['>=', ['zoom'], 5],
              ['<', ['zoom'], 6],
              ['==', ['%', ['get', 'KM'], 100], 0],
              ['!=', ['get', 'KM'], 0]
            ],
            layout: {
              'text-field': ['get', 'KM'],
              'text-size': 12,
              'text-anchor': 'left',
              'text-offset': [1, 0],
              'text-font': ['Roboto Bold'],
              'text-allow-overlap': true
            },
            paint: {
              'text-color': '#494444',
              'text-halo-color': '#FFFFFF',
              'text-halo-width': 1.5
            }
          });

          // Layer 8: No Freedom Camping Areas (Polygons)
          map.current.addSource('no-camping', {
            type: 'geojson',
            data: `${apiBaseUrl}/api/ArcGISProxy/layer/8/query?where=1=1&outFields=*&returnGeometry=true`
          });

          map.current.addLayer({
            id: 'no-camping-fill',
            type: 'fill',
            source: 'no-camping',
            paint: {
              'fill-color': '#FF0000',
              'fill-opacity': 0.2
            }
          });

          map.current.addLayer({
            id: 'no-camping-outline',
            type: 'line',
            source: 'no-camping',
            paint: {
              'line-color': '#FF0000',
              'line-width': 1
            }
          });

          if (process.env.NODE_ENV === 'development') {
            console.debug('Te Araroa trail layers added successfully');
          }

          // Call onMapLoad callback if provided
          if (onMapLoadRef.current && map.current) {
            onMapLoadRef.current(map.current);
          }
        });

        // Handle map clicks
        if (onMapClick) {
          map.current.on('click', (e) => {
            if (onMapClickRef.current) {
              onMapClickRef.current(e);
            }
          });
        }
      } catch (error) {
        console.error('Failed to load Te Araroa map:', error);
      }
    };

    initMap();

    // Cleanup on unmount
    return () => {
      if (map.current) {
        map.current.remove();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount. Props are intentionally not included as this is initialization logic.

  return (
    <div
      ref={mapContainer}
      style={{ height }}
      className={`w-full border border-gray-300 rounded-md ${className}`}
    />
  );
};

export default TeAraroaMap;
